import 'maplibre-gl/dist/maplibre-gl.css';
import type { Point } from 'geojson';
import { setWorkerUrl, type ExpressionSpecification, type Map as MaplibreMap, type MapGeoJSONFeature } from 'maplibre-gl';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, NavigationControl, Source, type MapLayerMouseEvent, type MapRef, type ViewStateChangeEvent } from 'react-map-gl/maplibre';
import { ALERT_STYLE } from '../config/alerts';
import { CAMERA, MAP } from '../config/constants';
import { STATION_PIN_COLOR } from '../config/gauges';
import { fimStore } from '../data/fimStore';
import { gaugeStore } from '../data/gaugeStore';
import { useAlertsQuery, useVisibleAlerts } from '../hooks/useAlerts';
import { useFIM, useVisibleGauges } from '../hooks/useExternalStores';
import { useHillCountryQuery } from '../hooks/useHillCountry';
import { useStationsQuery } from '../hooks/useStations';
import { useUserLocation } from '../hooks/useUserLocation';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { cameraDistanceForLatSpan } from '../lib/cameraDistance';
import { bboxToRegion, type Region } from '../lib/geo';
import { openDetail, useAppStore } from '../store/appStore';
import { useViewportStore } from '../store/viewportStore';
import { alertIconId, haloIconId, registerMapIcons, SEARCH_PIN_ICON } from './icons';
import { alertPointsFC, alertPolygonsFC, EMPTY_FC, gaugePointsFC, searchPinsFC, stationPointsFC } from './layerData';
import { mapController } from './mapController';
import { installSlots, SLOT } from './slots';

// MapLibre 6 resolves its worker as `new URL('./maplibre-gl-worker.mjs', import.meta.url)`
// at runtime, which a bundler cannot see. Hand it the worker Vite builds instead.
setWorkerUrl(maplibreWorkerUrl);

const HILL_COUNTRY_COLOR = '#9c27b0';
const FIM_COLOR = '#007aff';
const INTERACTIVE_LAYERS = ['search-pins', 'stations-circle', 'gauges-circle', 'alert-icons', 'alerts-fill'];

/** Priority of a point or, for a cluster, of its most severe member. */
const PRIORITY_EXPR: ExpressionSpecification = ['coalesce', ['get', 'minPriority'], ['get', 'priority']];

/** Optional deep link for QA and sharing: ?lat=30.27&lng=-97.74&zoom=11. */
function initialViewFromUrl(): { longitude: number; latitude: number; zoom: number } {
  const fallback = { longitude: -98.0, latitude: 38.5, zoom: 3.5 };
  if (typeof window === 'undefined') return fallback;
  const q = new URLSearchParams(window.location.search);
  const lat = Number(q.get('lat'));
  const lng = Number(q.get('lng'));
  const zoom = Number(q.get('zoom'));
  if (Number.isFinite(lat) && Number.isFinite(lng) && q.has('lat') && q.has('lng')) {
    return { latitude: lat, longitude: lng, zoom: Number.isFinite(zoom) && q.has('zoom') ? zoom : 10 };
  }
  return fallback;
}

function regionFromMap(map: MaplibreMap): Region {
  const b = map.getBounds();
  return bboxToRegion({ minLat: b.getSouth(), maxLat: b.getNorth(), minLon: b.getWest(), maxLon: b.getEast() });
}

export function MapView() {
  const mapRef = useRef<MapRef>(null);
  const [styleReady, setStyleReady] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const settleTimer = useRef<number | undefined>(undefined);
  const [initialView] = useState(initialViewFromUrl);

  const { data: alerts } = useAlertsQuery();
  const visibleAlerts = useVisibleAlerts(alerts);
  const hillCountryVisible = useAppStore((s) => s.hillCountryVisible);
  const { data: hillCountry } = useHillCountryQuery(hillCountryVisible);
  const { data: stations } = useStationsQuery();
  const gauges = useVisibleGauges();
  const fim = useFIM();
  const showAllGauges = useAppStore((s) => s.showAllGauges);
  const searchPins = useAppStore((s) => s.searchPins);
  const userLocation = useAppStore((s) => s.userLocation);
  const cameraDistance = useViewportStore((s) => s.cameraDistance);
  const setViewport = useViewportStore((s) => s.setViewport);
  const bumpSettle = useViewportStore((s) => s.bumpSettle);
  const setMapReady = useViewportStore((s) => s.setMapReady);

  useUserLocation(styleReady);

  const alertPolygons = useMemo(() => alertPolygonsFC(visibleAlerts), [visibleAlerts]);
  const alertPoints = useMemo(() => alertPointsFC(visibleAlerts), [visibleAlerts]);
  const gaugePoints = useMemo(() => gaugePointsFC(gauges, showAllGauges), [gauges, showAllGauges]);
  const stationPoints = useMemo(() => (stations ? stationPointsFC(stations) : EMPTY_FC), [stations]);
  const pinPoints = useMemo(() => searchPinsFC(searchPins), [searchPins]);

  // Camera-driven work, 300 ms after the camera settles (CombinedMapView.onMapCameraChange).
  const runCameraWork = useCallback((region: Region, distance: number) => {
    fimStore.schedule(region.center, distance);
    if (distance <= CAMERA.maxGaugeFetchDistance) {
      gaugeStore.updateVisibleRegion(region);
      gaugeStore.requestLoad(region.center, distance);
    }
    bumpSettle();
  }, [bumpSettle]);

  const handleViewportChange = useCallback(
    (map: MaplibreMap) => {
      const region = regionFromMap(map);
      const distance = cameraDistanceForLatSpan(region.latDelta);
      setViewport(region, distance, map.getZoom());
      window.clearTimeout(settleTimer.current);
      settleTimer.current = window.setTimeout(() => runCameraWork(region, distance), CAMERA.debounceMs);
    },
    [runCameraWork, setViewport],
  );

  const onLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    installSlots(map);
    registerMapIcons(map);
    mapController.attach(map);
    setStyleReady(true);
    setMapReady(true);
    handleViewportChange(map);
  }, [handleViewportChange, setMapReady]);

  const onMoveEnd = useCallback((e: ViewStateChangeEvent) => handleViewportChange(e.target), [handleViewportChange]);

  useEffect(() => {
    return () => {
      window.clearTimeout(settleTimer.current);
      const map = mapRef.current?.getMap();
      if (map) mapController.detach(map);
    };
  }, []);

  // Coming back to the tab re-prioritizes stale gauge statuses.
  useVisibilityRefresh(() => gaugeStore.refresh());

  const onClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const features = e.features ?? [];
      const byLayer = (id: string): MapGeoJSONFeature | undefined => features.find((f) => f.layer.id === id);

      // Hit precedence follows the draw order: pins, stations, gauges, alert icons, then the polygon.
      const pin = byLayer('search-pins');
      if (pin) {
        openDetail({ kind: 'pin', pinId: String(pin.properties.id) });
        return;
      }
      const station = byLayer('stations-circle');
      if (station) {
        openDetail({ kind: 'station', stationId: String(station.properties.id) });
        return;
      }
      const gaugeFeature = byLayer('gauges-circle');
      if (gaugeFeature) {
        const gauge = gaugeStore.visibleGauges.find((g) => g.id === String(gaugeFeature.properties.id));
        if (gauge) openDetail({ kind: 'gauge', gauge });
        return;
      }
      const icon = byLayer('alert-icons');
      if (icon) {
        const [lng, lat] = (icon.geometry as Point).coordinates;
        if (icon.properties.cluster) {
          mapController.zoomInOn({ lat, lng }, false);
        } else {
          mapController.zoomInOn({ lat, lng }, true);
          openDetail({ kind: 'alert', alertId: String(icon.properties.id) });
        }
        return;
      }
      const fill = byLayer('alerts-fill');
      if (fill) openDetail({ kind: 'alert', alertId: String(fill.properties.id) });
    },
    [],
  );

  const gaugesVisible = cameraDistance <= CAMERA.maxGaugeFetchDistance;
  const crestLabelsVisible = gaugesVisible && cameraDistance < CAMERA.crestLabelVisibilityDistance;
  const stationsVisible = cameraDistance <= CAMERA.maxStationDisplayDistance;
  const thcVisible = hillCountryVisible && cameraDistance <= CAMERA.thcMaxDistance;
  const thcStrokeVisible = thcVisible && cameraDistance <= CAMERA.thcStrokeMaxDistance;
  const fimStrokeVisible = cameraDistance <= CAMERA.fimStrokeMaxDistance;
  const vis = (on: boolean) => (on ? 'visible' : 'none') as 'visible' | 'none';

  return (
    <Map
      ref={mapRef}
      initialViewState={initialView}
      mapStyle={MAP.style}
      minZoom={MAP.minZoom}
      maxZoom={MAP.maxZoom}
      maxBounds={[MAP.maxBounds[0][0], MAP.maxBounds[0][1], MAP.maxBounds[1][0], MAP.maxBounds[1][1]]}
      dragRotate={false}
      pitchWithRotate={false}
      touchPitch={false}
      attributionControl={{ compact: true }}
      interactiveLayerIds={styleReady ? INTERACTIVE_LAYERS : undefined}
      cursor={cursor}
      onMouseEnter={() => setCursor('pointer')}
      onMouseLeave={() => setCursor(undefined)}
      onLoad={onLoad}
      onMoveEnd={onMoveEnd}
      onClick={onClick}
      style={{ position: 'absolute', inset: 0 }}
    >
      <NavigationControl position="bottom-left" showCompass={false} />

      {styleReady && (
        <>
          {/* Texas Hill Country watersheds: purple 12% fill, 1.5 px stroke within 500 km, hidden past 5,800 km. */}
          <Source id="hill-country" type="geojson" data={hillCountry ?? EMPTY_FC}>
            <Layer
              id="hill-country-fill"
              type="fill"
              beforeId={SLOT.hillCountry}
              filter={['==', ['geometry-type'], 'Polygon']}
              layout={{ visibility: vis(thcVisible) }}
              paint={{ 'fill-color': HILL_COUNTRY_COLOR, 'fill-opacity': 0.12 }}
            />
            <Layer
              id="hill-country-line"
              type="line"
              beforeId={SLOT.hillCountry}
              filter={['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'LineString']]}
              layout={{ visibility: vis(thcStrokeVisible) }}
              paint={{ 'line-color': HILL_COUNTRY_COLOR, 'line-width': 1.5 }}
            />
          </Source>

          {/* NOAA FIM inundation: blue 32% fill, stroke within 50 km. Holes are preserved by MapLibre. */}
          <Source id="fim" type="geojson" data={fim.polygons}>
            <Layer id="fim-fill" type="fill" beforeId={SLOT.fim} paint={{ 'fill-color': FIM_COLOR, 'fill-opacity': 0.32 }} />
            <Layer
              id="fim-line"
              type="line"
              beforeId={SLOT.fim}
              layout={{ visibility: vis(fimStrokeVisible) }}
              paint={{ 'line-color': FIM_COLOR, 'line-width': 1.5 }}
            />
          </Source>

          {/* NWS alert polygons: 24% fill with a 2 px stroke in the event color. */}
          <Source id="alert-polygons" type="geojson" data={alertPolygons}>
            <Layer id="alerts-fill" type="fill" beforeId={SLOT.alerts} paint={{ 'fill-color': ['get', 'color'], 'fill-opacity': 0.24 }} />
            <Layer id="alerts-line" type="line" beforeId={SLOT.alerts} paint={{ 'line-color': ['get', 'color'], 'line-width': 2 }} />
          </Source>

          {/* Tide stations: light blue 16 px pins within 2,000 km. */}
          <Source id="stations" type="geojson" data={stationPoints}>
            <Layer
              id="stations-circle"
              type="circle"
              beforeId={SLOT.stations}
              layout={{ visibility: vis(stationsVisible) }}
              paint={{ 'circle-radius': 8, 'circle-color': STATION_PIN_COLOR, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2 }}
            />
          </Source>

          {/* Gauges: 20 px circle in the observed category color, crest labels within 1,000 km. */}
          <Source id="gauges" type="geojson" data={gaugePoints}>
            <Layer
              id="gauges-circle"
              type="circle"
              beforeId={SLOT.gauges}
              layout={{ visibility: vis(gaugesVisible) }}
              paint={{
                'circle-radius': 10,
                'circle-color': ['get', 'color'],
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2,
              }}
            />
            <Layer
              id="gauges-label-top"
              type="symbol"
              beforeId={SLOT.gauges}
              filter={['all', ['==', ['get', 'isFlooding'], 1], ['!=', ['get', 'crestLabel'], '']]}
              layout={{
                visibility: vis(crestLabelsVisible),
                'text-field': ['get', 'crestLabel'],
                'text-font': ['Noto Sans Bold'],
                'text-size': 12,
                'text-anchor': 'bottom',
                'text-offset': [0, -1.1],
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              }}
              paint={{ 'text-color': ['get', 'crestColor'], 'text-halo-color': 'rgba(0,0,0,0.72)', 'text-halo-width': 2.5 }}
            />
            <Layer
              id="gauges-label-bottom"
              type="symbol"
              beforeId={SLOT.gauges}
              filter={['all', ['==', ['get', 'isFlooding'], 1], ['!=', ['get', 'nextLabel'], '']]}
              layout={{
                visibility: vis(crestLabelsVisible),
                'text-field': ['get', 'nextLabel'],
                'text-font': ['Noto Sans Bold'],
                'text-size': 12,
                'text-anchor': 'top',
                'text-offset': [0, 1.1],
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              }}
              paint={{ 'text-color': ['get', 'nextColor'], 'text-halo-color': 'rgba(0,0,0,0.72)', 'text-halo-width': 2.5 }}
            />
          </Source>

          {/* Alert icons: clustered; the cluster shows its most severe member's icon. Flash flood kinds pulse. */}
          <Source
            id="alert-points"
            type="geojson"
            data={alertPoints}
            cluster
            clusterRadius={50}
            clusterMaxZoom={14}
            clusterProperties={{ minPriority: ['min', ['get', 'priority']] }}
          >
            <Layer
              id="alert-halo"
              type="symbol"
              beforeId={SLOT.alertIcons}
              filter={['any', ['==', PRIORITY_EXPR, 1], ['==', PRIORITY_EXPR, 3]]}
              layout={{
                'icon-image': ['match', PRIORITY_EXPR, 1, haloIconId('flashFloodWarning'), 3, haloIconId('flashFloodWatch'), ''],
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
              }}
            />
            <Layer
              id="alert-icons"
              type="symbol"
              beforeId={SLOT.alertIcons}
              layout={{
                'icon-image': [
                  'match',
                  PRIORITY_EXPR,
                  ALERT_STYLE.flashFloodWarning.priority, alertIconId('flashFloodWarning'),
                  ALERT_STYLE.floodWarning.priority, alertIconId('floodWarning'),
                  ALERT_STYLE.flashFloodWatch.priority, alertIconId('flashFloodWatch'),
                  ALERT_STYLE.floodWatch.priority, alertIconId('floodWatch'),
                  ALERT_STYLE.floodAdvisory.priority, alertIconId('floodAdvisory'),
                  alertIconId('other'),
                ],
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
              }}
            />
          </Source>

          {/* Search pins with their place title. */}
          <Source id="search-pins-src" type="geojson" data={pinPoints}>
            <Layer
              id="search-pins"
              type="symbol"
              beforeId={SLOT.pins}
              layout={{
                'icon-image': SEARCH_PIN_ICON,
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
                'text-field': ['get', 'title'],
                'text-font': ['Noto Sans Bold'],
                'text-size': 12,
                'text-anchor': 'top',
                'text-offset': [0, 0.3],
                'text-optional': true,
              }}
              paint={{ 'text-color': '#171717', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 }}
            />
          </Source>
        </>
      )}

      {userLocation && (
        <Marker longitude={userLocation.lng} latitude={userLocation.lat} anchor="center">
          <div className="user-dot" aria-label="Your location" />
        </Marker>
      )}
    </Map>
  );
}
